/**
 * Tiny WebAudio key-click for the terminal. No samples — a filtered noise
 * tick per keystroke and a slightly deeper thock for Enter, quiet enough
 * to read as mechanical-keyboard ambience rather than an effect.
 *
 * Persisted via localStorage ("laurel_sound", default on); toggled by the
 * `sound` command.
 */

const STORAGE_KEY = "laurel_sound";

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  try {
    if (!ctx) ctx = new AudioContext();
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {
    // no storage — the toggle just won't persist
  }
}

/** A short filtered-noise tick; pitch wobbles so runs of keys don't buzz. */
export function keyClick(isEnter = false): void {
  if (!soundEnabled()) return;
  const ac = audioCtx();
  if (!ac) return;

  const now = ac.currentTime;
  const dur = isEnter ? 0.05 : 0.03;

  const noise = ac.createBufferSource();
  const len = Math.ceil(ac.sampleRate * dur);
  const buffer = ac.createBuffer(1, len, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  noise.buffer = buffer;

  const filter = ac.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = (isEnter ? 900 : 1800) + Math.random() * 500;
  filter.Q.value = 1.2;

  const gain = ac.createGain();
  gain.gain.setValueAtTime(isEnter ? 0.09 : 0.055, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

  noise.connect(filter).connect(gain).connect(ac.destination);
  noise.start(now);
  noise.stop(now + dur);
}
